import { useWorkspace } from "@/state/workspaceStore";
import { IconFilePlus } from "@/components/icons";
import { FileDropZone } from "./FileDropZone";
import { JTechPromoPanel } from "./JTechPromoPanel";
import { TrustBar } from "./TrustBar";

const HERO_SUPPORT =
  "A free document tool that works in your browser. Your files stay on your device.";

function Hero({ center }: { center?: boolean }) {
  return (
    <div className={center ? "text-center" : ""}>
      <h1
        className={[
          "font-extrabold tracking-tight text-slate-900",
          center
            ? "text-[clamp(2rem,9vw,3rem)] leading-[1.08]"
            : "max-w-[680px] text-[clamp(2.25rem,3vw,3.2rem)] leading-[1.05]",
        ].join(" ")}
      >
        Open PDFs. Edit Word files. <br className="hidden sm:inline" />
        Keep them private.
      </h1>
      <p
        className={[
          "text-slate-500",
          center
            ? "mx-auto mt-3 max-w-[32rem] text-base"
            : "mt-3 max-w-none whitespace-nowrap text-lg font-medium xl:text-xl",
        ].join(" ")}
      >
        {HERO_SUPPORT}
      </p>
    </div>
  );
}

function NewDocButton({ full }: { full?: boolean }) {
  const newBlankDocx = useWorkspace((s) => s.newBlankDocx);
  return (
    <button
      type="button"
      onClick={() => newBlankDocx()}
      className={[
        "inline-flex items-center justify-center gap-2 rounded-xl border-2 border-brand-500 bg-white font-semibold text-brand-600 transition-colors hover:bg-brand-50 active:bg-brand-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600",
        full ? "min-h-[52px] w-full px-6" : "px-5 py-2.5 text-sm",
      ].join(" ")}
    >
      <IconFilePlus className="h-5 w-5" aria-hidden="true" />
      New Word document
    </button>
  );
}

/** Empty-state landing: two-column card on desktop, single column on mobile. */
export function LandingPage() {
  return (
    <div className="jdoc-scroll h-full overflow-y-auto bg-slate-50/70">
      {/* ---------- Desktop / large screens ---------- */}
      <div className="hidden min-h-full place-items-center px-8 py-[clamp(12px,2.5vh,32px)] lg:grid">
        <div className="w-full max-w-[1320px]">
          <div className="overflow-hidden rounded-[20px] border border-slate-200/80 bg-white shadow-[0_12px_36px_rgba(18,35,67,0.10)]">
            <div className="grid grid-cols-[minmax(0,1.7fr)_minmax(360px,0.95fr)] gap-8 p-6 pt-8 xl:p-8 xl:pt-10">
              <div className="flex flex-col justify-center">
                <Hero />
                <div className="mt-4">
                  <FileDropZone variant="desktop" />
                </div>
                <div className="mt-2.5">
                  <NewDocButton />
                </div>
              </div>
              <JTechPromoPanel />
            </div>
          </div>
          <div className="mt-2">
            <TrustBar variant="desktop" />
          </div>
        </div>
      </div>

      {/* ---------- Mobile / tablet ---------- */}
      <div className="min-h-full px-4 py-6 lg:hidden">
        <div className="mx-auto w-full max-w-[720px]">
          <div className="overflow-hidden rounded-[18px] border border-slate-200/80 bg-white shadow-[0_12px_36px_rgba(18,35,67,0.10)]">
            <section className="p-5 sm:p-6">
              <Hero center />
              <div className="mt-6">
                <FileDropZone variant="mobile" />
              </div>
              <div className="mt-4">
                <NewDocButton full />
              </div>
            </section>
            <div className="px-5 pt-1 sm:px-6">
              <JTechPromoPanel compact />
            </div>
            <div className="px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
              <TrustBar variant="mobile" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
